import { useContexts } from "@/hooks/useContexts";
import { useSDK } from "@/hooks/useSDK";
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

const MagicLoginVerifyPage: React.FC = () => {
  const { sdk } = useSDK();
  const { authDispatch: dispatch } = useContexts();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [errorMessage, setErrorMessage] = useState<string>("");
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const redirect_uri = searchParams.get("redirect_uri");

  const login = async (token: string): Promise<void> => {
    try {
      if (!token) {
        throw new Error("No verification token provided");
      }
      const result = await sdk.callRawAPI(
        "/v1/api/contentgap/customer/lambda/verify",
        { token },
        "POST"
      );

      if (!result.error) {
        dispatch({
          type: "LOGIN",
          payload: result as any,
        });
        setStatus("success");

        setTimeout(() => navigate(redirect_uri ?? `/customer/login`), 1500); // Delay for UX
      } else {
        throw new Error(result.error || "Verification failed");
      }
    } catch (error: any) {
      console.error("Verification error:", error);
      setStatus("error");
      setErrorMessage(
        error.message || "Failed to verify email. Please try again."
      );
    }
  };

  useEffect(() => {
    const token = search.get("token") ?? "";
    (async () => {
      await login(token);
    })();
  }, [search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center transform transition-all duration-300 hover:scale-105">
        <div className="mb-6">
          {/* Placeholder for logo - replace with actual logo */}
        </div>
        {status === "loading" && (
          <>
            <div className="w-12 h-12 mx-auto border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <h2 className="mt-4 text-2xl font-semibold text-gray-800">
              Verifying your email...
            </h2>
            <p className="mt-2 text-gray-600">
              Please wait while we confirm your account.
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <svg
              className="w-16 h-16 mx-auto text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <h2 className="mt-4 text-2xl font-semibold text-gray-800">
              Verification Successful!
            </h2>
            <p className="mt-2 text-gray-600">Redirecting to login...</p>
          </>
        )}
        {status === "error" && (
          <>
            <svg
              className="w-16 h-16 mx-auto text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <h2 className="mt-4 text-2xl font-semibold text-gray-800">
              Verification Failed
            </h2>
            <p className="mt-2 text-gray-600">{errorMessage}</p>
            <button
              onClick={() => navigate("/customer/login")}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Return to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MagicLoginVerifyPage;
